/**
 * Nastavení layoutu (jen ADMIN).
 *
 * Od chvíle, kdy je zdrojem pravdy databáze, je tenhle panel **jediné místo, kde jde
 * rozložení zálohovat, přenést jinam nebo vrátit zpátky**. Dřív tuhle roli hrál
 * `defaults.ts` v gitu — a právě to, že se rozešel s DB, celou přestavbu vyvolalo.
 *
 * Plochy se berou z `GET /api/layouts`, ne z natvrdo psaného seznamu: panel tak ukáže
 * i plochu, kterou kód nezná, a hlavně **pozná chybějící**.
 */

import React, { useRef, useState } from "react";
import { Download, FileUp, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { createLayout, putLayout, resetLayout, setLayoutSeed } from "@client/api/layouts";
import { buildLayoutFile, layoutFileName, parseLayoutFile } from "@client/lib/layoutFile";
import { useLayoutStore, type LayoutEntry } from "@client/store/layout";
import { useUIStore } from "@client/store/ui";
import { Button } from "@client/components/ui/button";

/** Stažení JSONu. Mechanika z `RouterOsTabPanel.tsx:57-74`, jen bez cesty přes server. */
const stahni = (jmeno: string, obsah: string): void => {
  const url = URL.createObjectURL(new Blob([obsah], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = jmeno;
  a.click();
  URL.revokeObjectURL(url);
};

const exportText = (key: string, e: LayoutEntry): string =>
  JSON.stringify(buildLayoutFile(key, e.name, e.doc, new Date()), null, 2);

export const LayoutTabPanel: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const editMode = useUIStore((s) => s.editMode);
  const setEditMode = useUIStore((s) => s.setEditMode);
  const byKey = useLayoutStore((s) => s.byKey);
  const status = useLayoutStore((s) => s.status);
  const refetchOne = useLayoutStore((s) => s.refetchOne);
  const load = useLayoutStore((s) => s.load);

  const [busy, setBusy] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const importKey = useRef<string | null>(null);

  const plochy = [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b));

  /**
   * Záchranná brzda místo historie verzí: před každou destruktivní akcí spadne aktuální
   * stav do Stažených. Nic to nestojí a zachrání to omyl, který by jinak nešel vrátit.
   */
  const zalohuj = (key: string, e: LayoutEntry): void => stahni(layoutFileName(key, new Date()), exportText(key, e));

  const obnov = async (key: string, e: LayoutEntry) => {
    if (!e.hasSeed) return;
    if (!window.confirm("Obnovit tovární rozložení? Změny uvidí všichni. Aktuální stav se pro jistotu stáhne.")) return;
    zalohuj(key, e);
    setBusy(key);
    try {
      const res = await resetLayout(key, e.version);
      if (!res.success) return void toast.error(res.message || "Obnovení selhalo.");
      await refetchOne(key);
      toast.success("Obnoveno na tovární verzi.");
    } finally {
      setBusy(null);
    }
  };

  const nastavTovarni = async (key: string, e: LayoutEntry) => {
    if (!window.confirm("Nastavit současný stav jako tovární? Tím se přepíše to, na co obnovuje „Obnovit tovární“.")) {
      return;
    }
    setBusy(key);
    try {
      const res = await setLayoutSeed(key, e.version);
      if (!res.success) return void toast.error(res.message || "Nastavení selhalo.");
      await refetchOne(key);
      toast.success("Tovární verze nastavena.");
    } finally {
      setBusy(null);
    }
  };

  const vyberSoubor = (key: string) => {
    importKey.current = key;
    importRef.current?.click();
  };

  const naimportuj = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const soubor = ev.target.files?.[0];
    // Reset hned: bez toho by druhý pokus s TÝMŽ souborem `change` nevyvolal.
    ev.target.value = "";
    const key = importKey.current;
    if (!soubor || !key) return;

    const parsed = parseLayoutFile(await soubor.text());
    if (!parsed.ok) return void toast.error(parsed.error);

    if (parsed.file.key !== key) {
      const ok = window.confirm(
        `Soubor je exportovaný z plochy „${parsed.file.key}“, importuješ ho do „${key}“. Pokračovat?`
      );
      if (!ok) return;
    }

    const cil = byKey.get(key);
    setBusy(key);
    try {
      if (cil) {
        zalohuj(key, cil);
        // ⚠ Verze se bere ze STORU, ne ze souboru — viz komentář v `layoutFile.ts`.
        const res = await putLayout(key, { name: parsed.file.name, version: cil.version, doc: parsed.file.doc });
        if (!res.success) return void toast.error(res.message || "Import selhal.");
      } else {
        const res = await createLayout({ key, name: parsed.file.name, doc: parsed.file.doc });
        if (!res.success) return void toast.error(res.message || "Založení plochy selhalo.");
      }
      await refetchOne(key);
      toast.success("Rozložení naimportováno.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="font-medium">Editace layoutu</div>
          <div className="text-xs text-muted-foreground">Globální — změny vidí všichni. Klávesa „E“ přepíná taky.</div>
        </div>
        <Button
          size="sm"
          variant={editMode ? "default" : "outline"}
          onClick={() => {
            setEditMode(!editMode);
            onClose?.();
          }}
        >
          {editMode ? "Vypnout edit mode" : "Zapnout edit mode"}
        </Button>
      </div>

      <div>
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">Plochy</span>
          {/* Zdroj pravdy patří říct nahlas — dřív si každý myslel, že je to v kódu. */}
          <span className="text-[11px] text-muted-foreground">Uložené v databázi, ne v kódu.</span>
        </div>

        {status === "error" && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs">
            <span>Seznam ploch se nepodařilo načíst.</span>
            <Button size="sm" variant="ghost" onClick={() => void load()}>
              Zkusit znovu
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-1">
          {plochy.map(([key, e]) => (
            <div key={key} className="rounded border border-border px-2 py-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">
                  {e.name}
                  {e.invalid && <span className="ml-2 text-xs text-status-serious">poškozené</span>}
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">{key}</span>
              </div>

              <div className="mb-1 text-[11px] text-muted-foreground">
                v{e.version} · {e.updatedBy ?? "nikdo neupravoval"} ·{" "}
                {new Date(e.updatedAt).toLocaleString("cs-CZ")}
                {!e.hasSeed && <span className="ml-1 text-status-warning">· bez tovární kopie</span>}
              </div>

              <div className="flex flex-wrap gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === key || e.invalid}
                  onClick={() => stahni(layoutFileName(key, new Date()), exportText(key, e))}
                >
                  <Download className="mr-1 size-3.5" />
                  Export
                </Button>
                <Button size="sm" variant="ghost" disabled={busy === key} onClick={() => vyberSoubor(key)}>
                  <FileUp className="mr-1 size-3.5" />
                  Import
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === key || !e.hasSeed}
                  title={e.hasSeed ? undefined : "Tahle plocha nemá tovární kopii."}
                  onClick={() => void obnov(key, e)}
                >
                  <RotateCcw className="mr-1 size-3.5" />
                  Obnovit tovární
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === key || e.invalid}
                  onClick={() => void nastavTovarni(key, e)}
                >
                  <Save className="mr-1 size-3.5" />
                  Nastavit jako tovární
                </Button>
              </div>
            </div>
          ))}
        </div>

        <input ref={importRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => void naimportuj(e)} />
      </div>
    </div>
  );
};

export default LayoutTabPanel;
